// Shared redaction primitives for the LogRocket URL and network sanitizers (LP-2622), as in lending.
// Policy: default to masking. A value is kept only under an allowlisted name (logrocket-pii-keys.ts) and only when the
// value itself does not look like PII, a credential or a token.
// Note: no regex lookbehind here; it is a parse-time SyntaxError on older Safari and would break the bundle.

import {SAFE_BODY_KEYS, SAFE_RESPONSE_ONLY_BODY_KEYS} from './logrocket-pii-keys';

export const REDACTED = '[REDACTED]';

/** Larger bodies are dropped rather than parsed so capture never costs the applicant main-thread time. */
export const MAX_SANITIZED_BODY_LENGTH = 200_000;

interface ValuePattern {
    regex: RegExp;
    // When true, group 1 is a leading non-digit guard (stand-in for lookbehind) that must be kept.
    keepLead: boolean;
}

/** Lending's value patterns. */
const VALUE_PATTERNS: ValuePattern[] = [
    // JWT / OAuth-style bearer tokens
    {regex: /eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]*/g, keepLead: false},
    // Email addresses, including URL-encoded @
    {regex: /[A-Z0-9._%+-]+(?:@|%40)[A-Z0-9.-]+\.[A-Z]{2,}/gi, keepLead: false},
    // SSN / TIN (123-45-6789, 123456789) and card numbers (15-19 digits, optional space/dash separators)
    {regex: /(^|[^\d])(\d{3}-\d{2}-\d{4}|\d{9}|(?:\d[ -]?){14,18}\d)(?!\d)/g, keepLead: true},
    // US phone numbers: (555) 123-4567, 555-123-4567, 555.123.4567, +1 5551234567, 5551234567
    {regex: /(^|[^\d])((?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})(?!\d)/g, keepLead: true},
];

/** Digit runs (SSN, account, routing, card, phone, OTP, ids) and email addresses. */
const SENSITIVE_PATTERN = /\d{4,}|[^\s@]+@[^\s@]+/;

/** Replaces PII-looking substrings (tokens, emails, SSN/TIN, card and phone numbers) inside a free-text value. */
export function redactSensitiveValues(value: string): string {
    if (!value) {
        return value;
    }
    return VALUE_PATTERNS.reduce((text, {regex, keepLead}) =>
        text.replace(regex, (match: string, lead: string) => keepLead ? `${lead}${REDACTED}` : REDACTED), value);
}

/** Lower-cases a key and strips separators so `first_name`, `First-Name` and `firstName` compare equal. */
export function normalizeKey(key: string): string {
    return (key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** True for short values with no digit run, email, token or other PII-looking text. Flags, null and numbers are safe. */
export function isSafeValue(value: unknown): boolean {
    if (value === null || typeof value === 'boolean') {
        return true;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }
    return typeof value === 'string' && value.length <= 100 && !SENSITIVE_PATTERN.test(value) &&
        redactSensitiveValues(value) === value;
}

/** True when a JSON body key is on the allowlist. Unknown keys resolve to false (masked). */
export function isSafeBodyKey(key: string | null, isResponse: boolean): boolean {
    const normalized = key === null ? '' : normalizeKey(key);
    return SAFE_BODY_KEYS.has(normalized) || (isResponse && SAFE_RESPONSE_ONLY_BODY_KEYS.has(normalized));
}

/**
 * Deep-copies a parsed JSON value, keeping the structure. A primitive is kept only when its own key is allowlisted and
 * the value looks harmless; every other value is replaced, so new or unknown keys are masked.
 */
export function redactJson(value: unknown, isResponse: boolean, key: string | null = null): unknown {
    if (Array.isArray(value)) {
        return value.map(item => redactJson(item, isResponse, key));
    }
    if (value !== null && typeof value === 'object') {
        const result: Record<string, unknown> = {};
        for (const [childKey, childValue] of Object.entries(value)) {
            result[childKey] = redactJson(childValue, isResponse, childKey);
        }
        return result;
    }
    if (value === null || value === undefined) {
        return value;
    }
    return isSafeBodyKey(key, isResponse) && isSafeValue(value) ? value : REDACTED;
}
